import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { City, QuarantineUniversity } from '../../../Models';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useEffect, useRef, useState } from 'react';
import Loading from '../../Shared/Loading';
import { isQuarantineCity } from '../../../Helpers';
import { QuarantineDataType } from '../Quarantine';

export default function RejectAndReplaceCitiesPopup({
  isOpen,
  onCancel,
  onConfirm,
  selectedRows,
  cities,
  showConfirmedCities,
  gptCityDetails
}: {
  onCancel: Function,
  isOpen: boolean,
  onConfirm: (replacementData: { cityId: string; replacedCityId: string | null }[]) => void,
  selectedRows: QuarantineDataType[],
  cities: City[],
  showConfirmedCities:boolean,
  gptCityDetails:any
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [replacementData, setReplacementData] = useState<{ cityId: string; replacedCityId: string | null }[]>([]);
  const [isCheckedAll, setIsCheckedAll] = useState<boolean[]>([]);
  const cancelButtonRef = useRef(null);

  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

 

  
  useEffect(() => {
    if (selectedRows.length > 0) {
      const initialData = selectedRows.map(row => ({
        cityId: row.id,
        replacedCityId: ''
      }));
      setReplacementData(initialData);
    }
    setIsCheckedAll(new Array(selectedRows.length).fill(true));
  }, [selectedRows]);
  


useEffect(() => {
  if (selectedRows.length === 1) {
    const baseCity = selectedRows[0];
    const newData: { cityId: string; replacedCityId: string | null }[] = [];

    const cityDetails = gptCityDetails?.[baseCity.id];
    let replacedCityId: string | null = '';

    if (cityDetails?.corrected_name === "Invalid") {
      replacedCityId = null;
    } else if (cityDetails?.corrected_name) {
      const matchedCity = cities.find(
        (city) =>
          city.name.toLowerCase() === cityDetails.corrected_name.toLowerCase() &&
          city.id !== baseCity.id
      );

      if (matchedCity) {
        replacedCityId = matchedCity.id;
      }
    }

    newData.push({
      cityId: baseCity.id,
      replacedCityId,
    });

    if (
      !showConfirmedCities &&
      isCheckedAll[0] &&
      isQuarantineCity(baseCity) &&
      baseCity.replacedCities &&
      baseCity.replacedCities.length > 0
    ) {
      baseCity.replacedCities.forEach((repCity: any) => {
        newData.push({
          cityId: repCity.id,
          replacedCityId,
        });
      });
    }

    setReplacementData(newData);
  }
}, [selectedRows, cities, gptCityDetails, isCheckedAll, showConfirmedCities]);

  const handleClosePopup = () => {
    onCancel();
    setOpen(false);
  };

  const handleConfirm = () => {
    onConfirm(replacementData);
    setOpen(false);
  };

  const handleCitySelect = (index: number, cityId: string | null) => {
    const baseCity = selectedRows[index];
    const updatedData = [...replacementData];
    
    updatedData[index] = {
      ...updatedData[index],
      replacedCityId: cityId
    };
  
    if (!showConfirmedCities && isQuarantineCity(baseCity) && baseCity.replacedCities && isCheckedAll[index] && baseCity.replacedCities?.length > 0) {
      baseCity.replacedCities.forEach((repCity: any) => {
        const existingIndex = updatedData.findIndex(item => item.cityId === repCity.id);
  
        if (existingIndex !== -1) {
          updatedData[existingIndex].replacedCityId = cityId;
        } else {
          updatedData.push({
            cityId: repCity.id,
            replacedCityId: cityId
          });
        }
      });
    }
  
    setReplacementData(updatedData);
  };
  

  const handleRejectWithoutSelect = (index: number, checked: boolean) => {
    const baseCity = selectedRows[index];
    const updatedData = [...replacementData];
  
    updatedData[index] = {
      ...updatedData[index],
      replacedCityId: checked ? null : ''
    };
  
    if ( !showConfirmedCities && isQuarantineCity(baseCity) && isCheckedAll[index] && baseCity.replacedCities &&  baseCity.replacedCities?.length > 0) {
      baseCity.replacedCities.forEach((repCity: any) => {
        const existingIndex = updatedData.findIndex(item => item.cityId === repCity.id);
  
        if (existingIndex !== -1) {
          updatedData[existingIndex].replacedCityId = checked ? null : '';
        } else {
          updatedData.push({
            cityId: repCity.id,
            replacedCityId: checked ? null : ''
          });
        }
      });
    }
  
    setReplacementData(updatedData);
  };
  
  

  const isConfirmDisabled = () => {
    // Check if any row doesn't have a valid selection (either a city or reject checkbox)
    return replacementData.some(item => item.replacedCityId === '');
  };

  const handleCheckboxChange = (index: number) => {
    const updated = [...isCheckedAll];
    updated[index] = !updated[index];
    setIsCheckedAll(updated);
  };

  const getDisplayName = (index: number) => {
    const row = selectedRows[index];
    const details = gptCityDetails?.[row.id];
    return details?.corrected_name || row.name;
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        initialFocus={cancelButtonRef}
        onClose={handleClosePopup}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 ">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-darkbg dark:text-white shadow-xl transition-all w-full max-w-lg mx-auto max-h-[70vh] flex flex-col">
                <div className="p-6 overflow-y-auto flex-grow">
                  <div className="flex items-center mb-4">
                    <ExclamationTriangleIcon
                      className="h-6 w-6 text-red-600"
                      aria-hidden="true"
                    />
                    <div className="text-lg ml-2 font-semibold">
                      Reject City
                    </div>
                  </div>
                  <div className="bg-slate-100 dark:bg-darkbglight rounded-md mb-4 max-h-fit overflow-y-auto">
                    {selectedRows.map((city, index) => (
                      <div key={city.id} className="p-3 border-b border-gray-200 dark:border-gray-700 last:border-0">
                        <div className="font-semibold text-sm mb-2">
                          {getDisplayName(index)}
                        </div>
                        <div className="flex flex-col space-y-3">
                          <div className="relative">
                            <select
                              className="block w-full border-0 pl-3 py-2 text-gray-900 ring-1 ring-inset ring-gray-300 dark:ring-gray-500 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-blue-600 sm:text-sm bg-transparent dark:text-gray-300 dark:placeholder:text-gray-400 rounded-md"
                              value={replacementData[index]?.replacedCityId || ''}
                              onChange={(e) => handleCitySelect(index, e.target.value || null)}
                              disabled={replacementData[index]?.replacedCityId === null}
                            >

                              <option value="" className='dark:bg-darkbglight dark:text-white text-black'>Cities</option>
                              {cities.filter(rowCity => rowCity.id !== city.id).map(rowCity => (
                                <option key={Math.random()} value={rowCity.id} className='dark:bg-darkbglight dark:text-white text-black'>
                                  {rowCity.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="flex flex-col gap-2">
                            <div className='flex flex-row'>
                              <input
                                id={`reject-checkbox-${index}`}
                                type="checkbox"
                                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                                checked={replacementData[index]?.replacedCityId === null}
                                onChange={(e) => handleRejectWithoutSelect(index, e.target.checked)}
                              />
                              <label
                                htmlFor={`reject-checkbox-${index}`}
                                className="ml-2 text-sm text-gray-700 dark:text-gray-300"
                              >
                                Reject without selecting
                              </label>
                            </div>

                            {!showConfirmedCities && isQuarantineCity(city) && city.replacedCities && city.replacedCities?.length > 0 && <div className='flex flex-row'>
                              <input
                                id={`replace-common-checkbox-${index}`}
                                type="checkbox"
                                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                                checked={isCheckedAll[index] || false}
                                onChange={() => handleCheckboxChange(index)}
                              />
                              <label
                                htmlFor={`replace-common-checkbox-${index}`}
                                className="ml-2 text-sm text-gray-700 dark:text-gray-300"
                              >
                                Also Reject {city.replacedCities.length} common cities
                              </label>
                            </div>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-end mt-4">
                    <button
                      type="button"
                      className="bg-gray-200 text-gray-700 px-4 py-2 mr-2 rounded hover:bg-gray-300"
                      onClick={handleClosePopup}
                      ref={cancelButtonRef}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed"
                      onClick={handleConfirm}
                      disabled={isConfirmDisabled()}
                    >
                      Confirm
                    </button>
                  </div>
                </div>

                {/* Loading */}
                {loading && <Loading />}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

  